import { useEffect, useState } from 'react'
import { FormikConfig } from 'formik'
import { notification } from '@/shared/helpers/notificationMessages'
import { errorHandler } from '@/shared/lib/errorHandler'
import { useTranslation } from 'react-i18next'
import { EditPromotionFormState } from '../model/types'
import { editPromotionFormDefault } from './consts'
import { useEditPromotionApi } from '../api/edit-promotion-form-api'
import { usePromotionById } from '@/entities/Promotions'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { useResidentialsByPage } from '@/entities/Residential'
import { useDebounce } from '@/shared/lib/useDebounce'

export const useEditPromotionForm = (currentPromotionId: number, onCloseModal: () => void) => {
  const [initialValues, setInitialValues] = useState<EditPromotionFormState>(
    () => editPromotionFormDefault,
  )
  const { t } = useTranslation(['common'])
  const { language } = useLanguageStore()
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchValue = useDebounce(searchQuery, 500)

  const {
    data: currentPromotion,
    isLoading: isLoadingPromotion,
    isError: isErrorPromotion,
  } = usePromotionById(currentPromotionId)

  const {
    mutateAsync,
    isPending: isLoadingEditPromotion,
    isSuccess: isSuccessEditPromotion,
  } = useEditPromotionApi()

  const selectedProjectsIds = initialValues.residential_projects?.map(id => Number(id)) || []

  const { data: selectedResidentials, isLoading: isLoadingSelected } = useResidentialsByPage({
    limit: 100,
    offset: 0,
    language,
    projectsId: selectedProjectsIds,
    checked_manual: true,
  })

  const { data: defaultResidentials, isLoading: isLoadingDefaults } = useResidentialsByPage({
    limit: 100,
    offset: 0,
    search: debouncedSearchValue,
    language,
    checked_manual: true,
  })

  const submitForm: FormikConfig<EditPromotionFormState>['onSubmit'] = async values => {
    try {
      await mutateAsync(values)
      onCloseModal()
    } catch (error) {
      errorHandler(error, t('common:notification_messages.edit_promotion_error'))
    }
  }

  useEffect(() => {
    if (currentPromotion) {
      setInitialValues(() => {
        const {
          id,
          name,
          start_date,
          end_date,
          is_permanent,
          thumbnail,
          description,
          residential_projects,
        } = currentPromotion

        return {
          id,
          name,
          start_date: start_date ? new Date(start_date as string) : '',
          end_date: end_date ? new Date(end_date as string) : null,
          is_permanent,
          thumbnail: thumbnail,
          description,
          residential_projects: residential_projects.length
            ? residential_projects.map(item => String(item.id))
            : [],
        }
      })
    }
  }, [currentPromotion])

  useEffect(() => {
    if (isSuccessEditPromotion) {
      notification.success(
        t('common:notification_titles.success'),
        t('common:notification_messages.edit_promotion_success'),
      )
    }
  }, [isSuccessEditPromotion])

  const allResidentials = [
    ...(selectedResidentials?.data || []),
    ...(defaultResidentials?.data || []),
  ]

  const allOptions = allResidentials
    .filter((residential, index, array) => index === array.findIndex(r => r.id === residential.id))
    .map(residential => ({
      value: residential.id.toString(),
      label: residential.name,
    }))

  return {
    initialValues,
    isLoadingEditPromotion,
    isLoadingPromotion,
    isErrorPromotion,
    submitForm,
    isLoadingSelected,
    isLoadingDefaults,
    allOptions,
    setSearchQuery,
  }
}
